/**
 * Utility functions for contract calculations and formatting
 * Used in watchlist groups compact card display
 */

/**
 * Calculate days remaining until expiration from today
 * @param {string} expirationDate - ISO date string (YYYY-MM-DD)
 * @returns {number} Days until expiration (0 if expired)
 */
export const calculateDaysUntilExpiration = (expirationDate) => {
  if (!expirationDate) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day

  const expiration = new Date(expirationDate);
  expiration.setHours(0, 0, 0, 0);

  const diffTime = expiration - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays); // Never return negative
};

/**
 * Calculate progress toward price target
 * @param {number} initialPrice - Underlying price when contract was added
 * @param {number} currentPrice - Current underlying price
 * @param {number} targetPercent - Target % change (e.g., 20 for +20%, -5 for -5%)
 * @returns {object} Progress data with actualChange, targetChange, progress %, and remaining %
 */
export const calculateTargetProgress = (initialPrice, currentPrice, targetPercent) => {
  if (!initialPrice || !currentPrice || !targetPercent || initialPrice === 0) {
    return null;
  }

  // Calculate actual % change so far
  const actualChange = ((currentPrice - initialPrice) / initialPrice) * 100;

  // Target % change (already in percent format)
  const targetChange = parseFloat(targetPercent);

  // Calculate progress percentage
  const progress = targetChange !== 0 ? (actualChange / targetChange) * 100 : 0;

  // Calculate remaining % to reach target
  const remaining = targetChange - actualChange;

  return {
    actualChange: actualChange,      // e.g., 5.2 (current gain/loss %)
    targetChange: targetChange,      // e.g., 20 (target %)
    progress: progress,              // e.g., 26 (progress toward target)
    remaining: remaining,            // e.g., 14.8 (remaining % to target)
  };
};

/**
 * Find the closest simulation scenario to a given percentage
 * @param {array} scenarios - Array of simulation data with "Underlying Scenario % Change" field
 * @param {number} targetPercent - Target percentage to match (e.g., 14.8)
 * @returns {object|null} Closest scenario object or null if none found
 */
export const findClosestSimulationScenario = (scenarios, targetPercent) => {
  if (!scenarios || scenarios.length === 0 || targetPercent == null) {
    return null;
  }

  // Available scenarios from watchlist_analysis.py: ±5%, ±10%, ±20%, ±30%, ±50%, ±90%, +200%
  // Extract numeric values from scenario labels like "±10%"

  let closestScenario = null;
  let minDifference = Infinity;

  scenarios.forEach(scenario => {
    const scenarioLabel = scenario["Underlying Scenario % Change"];
    if (!scenarioLabel) return;

    // Parse scenario percentage (e.g., "±10%" → [10, -10], "+200%" → [200])
    const match = scenarioLabel.match(/([+-]?)(\d+(\.\d+)?)/);
    if (!match) return;

    const scenarioValue = parseFloat(match[2]);
    const sign = match[1];

    // For ± scenarios, check both positive and negative
    const values = sign === '±' ? [scenarioValue, -scenarioValue] : [parseFloat(sign + scenarioValue)];

    values.forEach(value => {
      const difference = Math.abs(value - targetPercent);
      if (difference < minDifference) {
        minDifference = difference;
        closestScenario = scenario;
      }
    });
  });

  return closestScenario;
};

/**
 * Format timestamp as relative time (e.g., "2h ago", "5m ago", "just now")
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Formatted relative time string
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Never';

  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;

  if (diffMs < 0) return 'Just now'; // Future time

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;

  // For older timestamps, show date
  return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Calculate equity percent change (always equals premium percent change)
 * @param {number} premiumPercent - Premium % change
 * @returns {number} Equity % change (same as premium %)
 */
export const calculateEquityPercent = (premiumPercent) => {
  // Equity % = Premium % because equity = premium × contracts × 100
  // The multiplier is constant, so % changes are identical
  return premiumPercent;
};

/**
 * Format currency value
 * @param {number} value - Numeric value
 * @returns {string} Formatted currency string (e.g., "$39.30")
 */
export const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '--';
  return `$${Number(value).toFixed(2)}`;
};

/**
 * Format percentage value
 * @param {number} value - Numeric value (e.g., 5.234)
 * @param {number} decimals - Number of decimal places (default 1)
 * @returns {string} Formatted percentage string (e.g., "5.2%")
 */
export const formatPercent = (value, decimals = 1) => {
  if (value == null || isNaN(value)) return '--';
  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format percentage with +/- sign
 * @param {number} value - Numeric value
 * @param {number} decimals - Number of decimal places (default 1)
 * @returns {string} Formatted percentage with sign (e.g., "+5.2%", "-3.1%")
 */
export const formatPercentWithSign = (value, decimals = 1) => {
  if (value == null || isNaN(value)) return '--';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(decimals)}%`;
};

/**
 * Get color class based on positive/negative value
 * @param {number} value - Numeric value
 * @returns {string} Tailwind color class
 */
export const getValueColorClass = (value) => {
  if (value == null || isNaN(value)) return 'text-gray-400';
  if (value > 0) return 'text-green-400';
  if (value < 0) return 'text-red-400';
  return 'text-gray-300';
};

/**
 * Parse target direction from target_percent_change value
 * @param {number} targetPercent - Target percentage (positive or negative)
 * @returns {object} {direction: 'gain'|'loss', value: absolute value}
 */
export const parseTargetDirection = (targetPercent) => {
  if (targetPercent == null) return null;

  const value = Math.abs(parseFloat(targetPercent));
  const direction = targetPercent >= 0 ? 'gain' : 'loss';

  return { direction, value };
};
